import React from "react";
import Typography from "aldoo-ra/Typography";
import Input from "aldoo-ra/Input";
import Checkbox from "aldoo-ra/Checkbox";
import FieldTypeSelector from "./components/FieldTypeSelector";
import FieldEditor from "./components/FieldEditor";

/**
 * Add Field Plugin
 * Adds a new field to content type and optionally to existing items
 */
export default {
  id: "cms-add-field",
  name: "Add Field",
  description: "Add a new field to content type",
  category: "type-operations",
  hidden: true,

  requiredCapabilities: {
    needsContentType: true,
    needsSelection: false,
    needsFieldAccess: false,
    needsTypeModification: true,
  },

  validate(context) {
    const errors = [];

    if (!context.contentType) {
      errors.push({ message: "Content type is required" });
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  },

  getInterface(context) {
    return {
      dialog: {
        title: "Add New Field",
        fields: [
          {
            id: "fieldName",
            type: "custom",
            label: "Field Name",
            required: true,
            render: (props) => (
              <div>
                <Typography variant="admin_label" className="mb-1">
                  Field Name
                  <span className="text-admin_error ml-1">*</span>
                </Typography>
                <Input
                  value={props.value || ''}
                  onChange={(e) => props.onChange(e.target.value)}
                  placeholder="e.g., aldooCore"
                  variant="admin"
                  className="w-full"
                />
                {props.error && (
                  <Typography variant="admin_caption" className="text-admin_error mt-1">
                    {props.error}
                  </Typography>
                )}
              </div>
            ),
            validate: (value) => {
              if (!value || !value.trim()) {
                return "Field name is required";
              }
              if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value)) {
                return "Field name must start with letter or underscore and contain only letters, numbers, and underscores";
              }
              if (context.typeUtils.getFieldDefinition(value)) {
                return "Field already exists";
              }
              return null;
            },
          },
          {
            id: "fieldLabel",
            type: "text",
            label: "Field Label (optional)",
            placeholder: "Human-readable label",
          },
          {
            id: "fieldType",
            type: "custom",
            label: "Field Type",
            required: true,
            render: (props) => (
              <FieldTypeSelector
                value={props.value}
                onChange={props.onChange}
                label="Field Type"
                required={true}
                error={props.error}
              />
            ),
          },
          {
            id: "defaultValue",
            type: "custom",
            label: "Default Value",
            visible: (values) => !!values.fieldType,
            render: (props) => (
              <FieldEditor
                fieldType={props.values.fieldType}
                value={props.value}
                onChange={props.onChange}
                label="Default Value (optional)"
                error={props.error}
              />
            ),
          },
          {
            id: "applyToExisting",
            type: "checkbox",
            label: "Apply default value to existing items",
            defaultValue: true,
            visible: (values) => !!values.defaultValue,
          },
        ],
      },

      summary: {
        beforeExecute: (values, context) => {
          const changes = [
            {
              description: `Add field "${values.fieldName}" (${values.fieldType})`,
              impact: "high",
            },
          ];

          if (values.applyToExisting && values.defaultValue !== undefined) {
            changes.push({
              description: `Set default value for all existing items`,
              impact: "medium",
            });
          }

          return {
            itemsAffected: values.applyToExisting ? context.selectedItems?.length || 0 : 0,
            fieldsAffected: [values.fieldName],
            changes,
            warnings: values.applyToExisting
              ? []
              : ["Existing items will not have this field populated"],
          };
        },

        afterExecute: (result) => {
          if (result.success) {
            return `Successfully added field "${result.data?.fieldName}"`;
          }
          return `Failed to add field: ${result.errors?.[0]?.error}`;
        },
      },
    };
  },

  async execute(context, params) {
    const { values } = params;
    const { fieldName, fieldLabel, fieldType, defaultValue, applyToExisting } = values;

    try {
      // Create the field definition
      const newField = {
        name: fieldName,
        label: fieldLabel || fieldName,
        type: fieldType,
      };

      // Add field to content type definition
      const updatedTypeDefinition = [
        ...(context.contentType.type_definition || []),
        newField,
      ];

      // Update the content type
      await context.controller.updateContentType(context.contentType._id, {
        type_definition: updatedTypeDefinition,
      });

      // If we should apply to existing items and have a default value
      if (applyToExisting && defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
        const allItems = await context.controller.getContentList(context.contentType._id);

        let itemsSucceeded = 0;
        let itemsFailed = 0;
        const errors = [];

        context.ui.showProgress(0, allItems.length, "Applying default value...");

        for (let i = 0; i < allItems.length; i++) {
          const item = allItems[i];

          try {
            const result = await context.controller.saveContent({
              _id: item._id,
              content: {
                ...item.content,
                [fieldName]: defaultValue,
              },
            }, false);

            if (result?.error) {
              itemsFailed++;
              errors.push({
                itemId: item._id,
                error: result.error,
              });
            } else {
              itemsSucceeded++;
            }
          } catch (error) {
            itemsFailed++;
            errors.push({
              itemId: item._id,
              error: error.message,
            });
          }

          context.ui.showProgress(i + 1, allItems.length, "Applying default value...");
        }

        return {
          success: itemsFailed === 0,
          itemsProcessed: allItems.length,
          itemsSucceeded,
          itemsFailed,
          errors: errors.length > 0 ? errors : undefined,
          data: { fieldName, fieldType },
        };
      }

      return {
        success: true,
        itemsProcessed: 0,
        itemsSucceeded: 0,
        itemsFailed: 0,
        data: { fieldName, fieldType },
      };
    } catch (error) {
      return {
        success: false,
        itemsProcessed: 0,
        itemsSucceeded: 0,
        itemsFailed: 0,
        errors: [{ error: error.message }],
      };
    }
  },
};
